import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// import ChartJS from '../chart_js'; // chart hidden for now
import vlog from '../../javascript/vlog';
import withV2HealthCheck from '../with_v2_health_check';

const getServerOffsetMs = (serverTime) => {
  if (!serverTime) return 0;
  const serverDate = new Date(serverTime);
  if (Number.isNaN(serverDate.getTime())) return 0;
  return serverDate.getTime() - Date.now();
};

const getLastUpdatedLabel = (serverOffsetMs = 0) => {
  const now = new Date(Date.now() + serverOffsetMs);
  const latestHour = new Date(now);
  latestHour.setMinutes(0, 0, 0);
  const diffMs = now - latestHour;
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes <= 0) return 'awhile ago';
  if (diffMinutes === 1) return '1 minute ago';
  return `${diffMinutes} minutes ago`;
};

function Analytics({
  source_request_id: sourceRequestId,
  views_labels: viewsLabels,
  server_time: serverTime,
}) {
  const [analyticsData, setAnalyticsData] = useState(null);
  // eslint-disable-next-line no-unused-vars -- chart hidden for now; setChartConfig still populated
  const [chartConfig, setChartConfig] = useState(null);
  const [hasError, setHasError] = useState(false);
  const serverOffsetMs = useMemo(() => getServerOffsetMs(serverTime), [serverTime]);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState(() => getLastUpdatedLabel(serverOffsetMs));
  const [isExpanded, setIsExpanded] = useState(() => {
    // Load saved preference from localStorage, default to true (expanded)
    const saved = localStorage.getItem('analyticsExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    const getSourceRequestAnalytics = async () => {
      try {
        // Use supabaseClient for PostgREST API
        const { data, error } = await window.supabaseClient
          .from('request_analytics')
          .select('*')
          .eq('migrate_id', sourceRequestId)
          .maybeSingle();

        if (error) {
          vlog.error('Error fetching request analytics:', error);
          setHasError(true);
          return;
        }

        if (!data) {
          vlog.debug('No analytics data found for this request');
          setHasError(true);
          return;
        }

        // supabaseClient returns single object with maybeSingle()
        const analytics = data;
        let accuratePitchCount = analytics.total_pitches_value || 0;

        try {
          const v2RequestId = analytics.request_id;

          if (v2RequestId) {
            // Query pitches API - only count sent pitches (not drafts)
            const { count, error: pitchError } = await window.supabaseClient
              .from('pitches')
              .select('*', { count: 'exact', head: true }) // head: true means just get count, no data
              .eq('request_id', v2RequestId)
              .not('sent_at', 'is', null); // Only sent pitches, not drafts

            if (pitchError) {
              vlog.error('Error fetching pitch count:', pitchError);
            } else {
              accuratePitchCount = count || 0;
              vlog.debug(`Pitch count from pitches API: ${accuratePitchCount}`);
            }
          }
        } catch (err) {
          vlog.error('Failed to fetch accurate pitch count:', err);
        }

        // Format for component expectations
        const formattedData = {
          views_count: {
            // Use only labels present in viewsLabels prop and fall back to raw analytics data if empty/zero
            ...(() => {
              const allowed = Array.isArray(viewsLabels) ? viewsLabels : [];
              const rawLabels = Array.isArray(analytics.views_labels) ? analytics.views_labels : [];
              const rawValues = Array.isArray(analytics.views_values) ? analytics.views_values : [];
              const filteredLabels = [];
              const filteredValues = [];
              rawLabels.forEach((label, idx) => {
                if (allowed.includes(label)) {
                  filteredLabels.push(label);
                  filteredValues.push(rawValues[idx] ?? 0);
                }
              });
              const shouldFallback = filteredValues.length === 0
                || filteredValues.every((value) => value === null || value === undefined || value === 0);
              return shouldFallback
                ? { labels: rawLabels, values: rawValues.map((value) => value ?? 0) }
                : { labels: filteredLabels, values: filteredValues };
            })(),
          },
          emails_sent: {
            value: analytics.emails_sent_value || 0,
            change: analytics.emails_sent_change || 0,
            arrow: analytics.emails_sent_arrow || 'up',
          },
          total_views: {
            value: analytics.total_views_value || 0,
            change: analytics.total_views_change || 0,
            arrow: analytics.total_views_arrow || 'up',
          },
          total_pitches: {
            value: accuratePitchCount,
            change: analytics.total_pitches_change || 0,
            arrow: analytics.total_pitches_arrow || 'up',
          },
        };

        setAnalyticsData(formattedData);

        // Compute dynamic suggestedMax from values
        const values = formattedData.views_count.values || [];
        const maxVal = values.length ? Math.max(...values) : 0;
        let avgDiff = 0;
        if (values.length > 1) {
          let sum = 0;
          for (let i = 1; i < values.length; i += 1) {
            sum += Math.abs(values[i] - values[i - 1]);
          }
          avgDiff = sum / (values.length - 1);
        }
        const baseSuggested = Math.ceil(maxVal + avgDiff);
        const suggestedMax = Math.max(baseSuggested, maxVal + (values[0] || 1));

        // Update chart config
        const chartConfiguration = {
          type: 'line',
          data: {
            labels: formattedData.views_count.labels,
            datasets: [
              {
                label: 'Daily View Count',
                data: formattedData.views_count.values,
                fill: false,
                tension: 0.1,
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            // v3+ plugin config
            plugins: {
              title: {
                display: true,
                text: 'Views in the Last 7 Days',
              },
            },
            // v3+ scales
            scales: {
              x: {
                grid: { display: false },
              },
              y: {
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                },
                grid: { display: false },
                title: {
                  display: true,
                  text: 'Views',
                },
              },
              // v2 fallbacks (ignored by v3)
              xAxes: [
                {
                  gridLines: { display: true },
                },
              ],
              yAxes: [
                {
                  ticks: {
                    suggestedMax,
                    beginAtZero: true,
                    precision: 0,
                  },
                  gridLines: { display: true },
                  scaleLabel: {
                    display: true,
                    labelString: 'Views',
                  },
                },
              ],
            },
          },
        };

        setChartConfig(chartConfiguration);
      } catch (error) {
        vlog.error('Analytics fetch failed:', error);
        setHasError(true);
      }
    };

    if (sourceRequestId) {
      getSourceRequestAnalytics();
    }
  }, [sourceRequestId, viewsLabels]);

  useEffect(() => {
    setLastUpdatedLabel(getLastUpdatedLabel(serverOffsetMs));
    const interval = setInterval(() => {
      setLastUpdatedLabel(getLastUpdatedLabel(serverOffsetMs));
    }, 60000);

    return () => clearInterval(interval);
  }, [serverOffsetMs]);

  const toggleExpanded = () => {
    const newValue = !isExpanded;
    setIsExpanded(newValue);
    // Persist preference to localStorage
    localStorage.setItem('analyticsExpanded', JSON.stringify(newValue));
  };

  const handleReadPitchesClick = (event) => {
    if (event) event.preventDefault();
    if (typeof document !== 'undefined') {
      const target = document.getElementById('your-pitches');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    if (typeof window !== 'undefined') {
      window.location.hash = 'your-pitches';
    }
  };

  // Hide component if there's an error, no data, or all metrics are zero (nothing useful to show)
  const shouldHideAnalytics = analyticsData
    && (analyticsData?.total_views?.value ?? 0) === 0
    && (analyticsData?.total_pitches?.value ?? 0) === 0
    && (analyticsData?.emails_sent?.value ?? 0) === 0;

  if (hasError || !analyticsData || shouldHideAnalytics) return null;

  return (
    <div className="d-none d-md-block">
      <div className="mt-5" />
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h2 className="fw-bold d-flex align-items-center mb-0">
          <span id="your-pitches">Analytics</span>
          <button
            type="button"
            className="btn btn-link btn-sm ms-2 text-decoration-none"
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse analytics' : 'Expand analytics'}
            style={{ fontSize: '1.2rem', padding: '0 0.5rem' }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        </h2>
        <div className="text-muted small d-flex align-items-center">
          <i className="fa-regular fa-clock me-1" />
          <span>
            Last Updated: {lastUpdatedLabel}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="card info-card">
        <div className="card-body">
          <div className="row pt-3 pb-3">
            <div className="col-md-4 d-flex">
              <div className="card p-3 metric-card flex-fill d-flex flex-column">
                <div className="metric-title">
                  <i className="fa-solid fa-envelope" /> Emails Sent
                </div>
                <div className="metric-subtext">
                  Making your post broader allows it to be sent to more experts, increasing visibility.
                </div>
                <div className="d-flex justify-content-between align-items-end mt-auto pt-3">
                  <div className="metric-value">{analyticsData?.emails_sent?.value}</div>
                </div>
              </div>
            </div>

            <div className="col-md-4 d-flex">
              <div className="card p-3 metric-card flex-fill d-flex flex-column">
                <div className="metric-title">
                  <i className="fa-solid fa-eye" /> Total Views
                </div>
                <div className="metric-subtext">
                  Sharing a post on social media boosts visibility and
                  improves your chances of getting quality pitches.
                </div>
                <div className="d-flex justify-content-between align-items-end mt-auto pt-3">
                  <div className="metric-value">{analyticsData?.total_views?.value}</div>
                </div>
              </div>
            </div>

            <div className="col-md-4 d-flex">
              <div className="card p-3 metric-card flex-fill d-flex flex-column">
                <div className="metric-title">
                  <div className="d-flex justify-content-between">
                    <span><i className="fa-solid fa-microphone" /> Total Pitches</span>
                    <a href="#your-pitches" className="small" onClick={handleReadPitchesClick}>
                      Read Pitches →
                    </a>
                  </div>
                </div>
                <div className="metric-subtext">
                  Adding questions and background info makes your post clearer and attracts more relevant pitches.
                </div>
                <div className="d-flex justify-content-between align-items-end mt-auto pt-3">
                  <div className="metric-value">{analyticsData?.total_pitches?.value}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Analytics chart hidden for now
      {isExpanded && chartConfig && (
        <>
          <div className="mb-2" />
          <div className="card info-card">
            <div className="card-body">
              <ChartJS height={240} config={chartConfig} />
            </div>
          </div>
        </>
      )}
      */}
    </div>
  );
}

Analytics.propTypes = {
  source_request_id: PropTypes.number.isRequired,
  views_labels: PropTypes.array.isRequired,
  server_time: PropTypes.string.isRequired,
};

// Wrap with V2 health check HOC. null fallback: an optional section of the request-detail
// page that already self-hides when its data is missing.
export default withV2HealthCheck(Analytics, null);
